import type { ChatMessage } from '@/lib/types';
import { generateTimestampTitle } from '@/lib/title-utils';
import type {PromptType} from "@/app/(chat)/api/chat/route";

export interface StoredChat {
  id: string;
  title: string;
  messages: ChatMessage[];
  userId?: string | null; // Optional user ID for conversation scoping
  companyName?: string | null; // Optional company name for conversation scoping
  userName?: string | null; // Optional user name for conversation scoping
  promptMode?: PromptType; // Track which prompt mode is used
  price: number; // Track conversation cost in dollars
  createdAt: string;
  updatedAt: string;
}

// Event system for real-time updates
class ChatStorageEventEmitter {
  private listeners: Set<() => void> = new Set();

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit() {
    this.listeners.forEach(listener => {
      listener();
    });
  }
}

export const chatStorageEvents = new ChatStorageEventEmitter();

// API-based functions that use server-side routes
export async function getChats(userId?: string | null, companyName?: string | null, userName?: string | null): Promise<StoredChat[]> {
  console.log('🔍 getChats called - loading from API...', { userId, companyName, userName });
  try {
    const url = new URL('/api/chats', window.location.origin);
    if (userId) {
      url.searchParams.set('userId', userId);
    }
    if (companyName) {
      url.searchParams.set('companyName', companyName);
    }
    if (userName) {
      url.searchParams.set('userName', userName);
    }
    
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.chats;
  } catch (error) {
    console.error('❌ Error loading chats from API:', error);
    return [];
  }
}

export async function getChat(id: string, userId?: string | null, companyName?: string | null, userName?: string | null): Promise<StoredChat | null> {
  try {
    const url = new URL(`/api/chats/${id}`, window.location.origin);
    if (userId) {
      url.searchParams.set('userId', userId);
    }
    if (companyName) {
      url.searchParams.set('companyName', companyName);
    }
    if (userName) {
      url.searchParams.set('userName', userName);
    }
    
    const response = await fetch(url.toString());
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.chat;
  } catch (error) {
    console.error('Error loading chat from API:', error);
    return null;
  }
}

export async function saveChat(chat: StoredChat, skipEventEmit = false): Promise<void> {
  try {
    const response = await fetch('/api/chats', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(chat),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Only emit events for operations that should trigger UI updates (like delete, title changes)
    // Skip for regular message saves to prevent infinite loops
    if (!skipEventEmit) {
      chatStorageEvents.emit();
    }
  } catch (error) {
    console.error('❌ Error saving chat via API:', error);
    throw error;
  }
}

export async function deleteChat(id: string): Promise<void> {
  try {
    const response = await fetch(`/api/chats/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    chatStorageEvents.emit();
  } catch (error) {
    console.error('Error deleting chat via API:', error);
    throw error;
  }
}

export function generateChatTitle(): string {
  // Always use timestamp-based title format
  return generateTimestampTitle();
}

export async function updateChatTitle(id: string, title: string): Promise<void> {
  try {
    const response = await fetch(`/api/chats/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    chatStorageEvents.emit();
  } catch (error) {
    console.error('Error updating chat title via API:', error);
    throw error;
  }
}


export async function clearAllChats(): Promise<void> {
  try {
    const response = await fetch('/api/chats', {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    chatStorageEvents.emit();
  } catch (error) {
    console.error('Error clearing chats via API:', error);
    throw error;
  }
}
